import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template3Props {
  data: ResumeData;
}

export const Template3: React.FC<Template3Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans">
      {/* Header with accent */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-8 mb-8">
        <h1 className="text-4xl font-bold mb-2">{data.personalInfo.name}</h1>
        <h2 className="text-xl opacity-90 mb-4">{data.personalInfo.title}</h2>
        
        <div className="flex flex-wrap gap-6 text-sm opacity-90">
          <span>{data.personalInfo.email}</span>
          <span>{data.personalInfo.phone}</span>
          <span>{data.personalInfo.location}</span>
          {data.personalInfo.linkedin && <span>{data.personalInfo.linkedin}</span>}
          {data.personalInfo.website && <span>{data.personalInfo.website}</span>}
        </div>
      </header>

      <div className="px-8">
        {/* Summary */}
        <section className="mb-8">
          <h3 className="text-xl font-bold text-indigo-600 mb-4 flex items-center">
            <span className="w-8 h-0.5 bg-indigo-600 mr-3"></span>
            Executive Summary
          </h3>
          <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">{data.summary}</p>
        </section>

        {/* Experience */}
        <section className="mb-8">
          <h3 className="text-xl font-bold text-indigo-600 mb-6 flex items-center">
            <span className="w-8 h-0.5 bg-indigo-600 mr-3"></span>
            Professional Experience
          </h3>
          
          {data.experience.map((job, index) => (
            <div key={index} className="mb-8 relative">
              {/* Timeline dot */}
              <div className="absolute left-0 top-2 w-3 h-3 bg-indigo-600 rounded-full"></div>
              <div className="ml-8">
                <div className="mb-3">
                  <h4 className="text-lg font-bold text-gray-900">{job.title}</h4>
                  <div className="flex justify-between items-center">
                    <p className="text-indigo-600 font-semibold">{job.company}</p>
                    <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                      {job.dates}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{job.location}</p>
                </div>
                
                <ul className="space-y-2">
                  {job.bullets.map((bullet, bulletIndex) => (
                    <li key={bulletIndex} className="flex items-start">
                      <span className="w-2 h-2 bg-indigo-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-gray-700">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </section>

        {/* Education & Skills Grid */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          {/* Education */}
          <section>
            <h3 className="text-xl font-bold text-indigo-600 mb-4 flex items-center">
              <span className="w-8 h-0.5 bg-indigo-600 mr-3"></span>
              Education
            </h3>
            
            <EducationFormatter 
              education={data.education} 
              className="text-gray-900"
            />
          </section>

          {/* Skills */}
          <section>
            <h3 className="text-xl font-bold text-indigo-600 mb-4 flex items-center">
              <span className="w-8 h-0.5 bg-indigo-600 mr-3"></span>
              Key Skills
            </h3>
            
            <div className="grid grid-cols-1 gap-2">
              {data.skills.map((skill, index) => (
                <div key={index} className="bg-indigo-50 text-indigo-700 px-3 py-2 rounded-md text-sm font-medium">
                  {skill}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};